<script lang="ts" setup>
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import ReservePrice from './_components/ReservePrice.vue';
import CapacityText from './_components/CapacityText.vue';
import TrackingButton from './_components/TrackingButton.vue';
import type TripPackage from '@itinerary/trip/TripPackage';
import TripCardSegment from '@components/util/trip/TripCardSegment/TripCardSegment.vue';

const {
    tripPackage,
    hideCapacity,
    hideInvoice,
    isSelected,
    customLabel,
    disableDetails,
} = defineProps<IProps>();

const emit = defineEmits<{
    'button-clicked': [pkg: TripPackage];
    mouseenter: [event: MouseEvent];
    mouseleave: [event: MouseEvent];
}>();

const PackageDetails = defineAsyncComponent(
    () => import('./_components/package-details/PackageDetails.vue')
);

const CompactSegments = defineAsyncComponent(
    () => import('@components/util/trip/TripCardSegment/CompactSegments.vue')
);

const itineraryStore = useItineraryStore();

interface IProps {
    tripPackage: TripPackage;
    isOpenDetails?: boolean;
    isSelected?: boolean;
    showPriceOnButton?: boolean;
    hideInvoice?: boolean;
    hideCapacity?: boolean;
    customLabel?: string;
    compact?: boolean;
    disableDetails?: boolean;
}

const isShowingDetails = defineModel<boolean>('details', { default: false });
const { isMobile, isDesktop } = useSize();
const analyticsStore = useAnalyticsStore();

const emitReserve = () => emit('button-clicked', tripPackage);

const nonReservable = computed(
    () => !tripPackage.reservable && !tripPackage.autoReserveEnabled
);
const cardLabel = computed(() => customLabel || tripPackage.label);

const reserve = async () => {
    if (!tripPackage.reserveButtonEnabled && !tripPackage.autoReserveEnabled) {
        swal({
            icon: 'warning',
            title: tripPackage?.alertText,
            confirmButtonText: 'باشه',
            text: tripPackage.reserveError,
        });
        return;
    }
    if (tripPackage.specialCondition && isDesktop.value) {
        await swal({
            icon: 'warning',
            title: 'توجه',
            confirmButtonText: 'باشه',
            text: tripPackage.specialCondition,
        });
    }
    emitReserve();
};

const maxHeight = computed(
    () => `${tripPackage?.allGroupedTrips?.length * 115 + 200}px`
);

const hideDetails = () => {
    isShowingDetails.value = false;
};

const showDetails = () => {
    if (isSelected) {
        return;
    }
    const searchForm = useSearchFormStore().currentSearchForm();
    analyticsStore.trackAll('site-details', {
        isMasir: tripPackage.isRoutePackage(),
        segmentServices: tripPackage.segments
            .map(s => s.trips[0]?.reserveType)
            .filter(Boolean),
        segmentCount: tripPackage.segments.length,
        ...searchForm.searchAttributes,
    });
    isShowingDetails.value = true;
};

const toggleDetails = () => {
    if (!disableDetails) {
        isShowingDetails.value ? hideDetails() : showDetails();
    }
};

const { reservableList } = storeToRefs(itineraryStore);

const isHovered = ref(false);

const trip = computed(() => reservableList.value?.[0] as TripPackage);

const capacityText = computed(() =>
    tripPackage?.capacity === 0
        ? ''
        : tripPackage?.capacityTextReplacement ||
          `${tripPackage?.capacityText} صندلی`
);

const showTrackingButton = computed(() => !isSelected);

const onHoverIn = (event: MouseEvent) => {
    isHovered.value = true;
    emit('mouseenter', event);
};
const onHoverOut = (event: MouseEvent) => {
    isHovered.value = false;
    emit('mouseleave', event);
};

const singleSegment = computed(
    () => tripPackage.segments?.length === 1 || tripPackage.isRoutePackage()
);
</script>

<template>
    <div
        class="trip-package-info"
        :class="{ shadow: !tripPackage.isRoutePackage() }"
    >
        <div v-if="cardLabel" class="card-label">
            <mr-typography size="small" weight="500" color="var(--yellow-700)">
                {{ cardLabel }}
            </mr-typography>
        </div>
        <article
            class="trip-card"
            :class="{
                'single-segment': singleSegment,
                'not-reservable': nonReservable,
                selected: isSelected,
            }"
            @mouseenter="onHoverIn"
            @mouseleave="onHoverOut"
            @click="toggleDetails"
        >
            <section class="trip-info">
                <div class="segments">
                    <CompactSegments v-if="compact" :pkg="tripPackage" />
                    <template v-else>
                        <TripCardSegment
                            v-for="(segment, key) in tripPackage?.segments"
                            :key
                            :segment
                            class="segment"
                            :vehicle-type="tripPackage.vehicleType"
                            :international="tripPackage.international"
                        >
                            <template #extra-detail>
                                <slot name="extra-detail" />
                            </template>
                        </TripCardSegment>
                    </template>
                </div>
                <div v-if="!isSelected" class="details">
                    <div
                        v-if="!nonReservable"
                        class="details-toggle desktop-only"
                        :class="{ active: isShowingDetails }"
                    >
                        <mr-button variant="transparent" size="medium">
                            جزئیات
                        </mr-button>
                        <mr-icon
                            class="dropdown-icon"
                            :icon="faChevronDown"
                            size="small"
                            color="var(--blue-400)"
                        />
                    </div>
                    <div class="labels">
                        <mr-chips
                            v-for="(label, index) in tripPackage.labels"
                            :key="label.title"
                            class="label"
                            :color="label.backgroundColor"
                            :style="{ order: label?.order ?? index }"
                        >
                            <mr-icon
                                v-if="label.icon"
                                size="x-small"
                                :icon="label.icon"
                                :color="label.color"
                            />
                            <mr-typography
                                size="small"
                                weight="500"
                                white-space="nowrap"
                                :color="label.color"
                            >
                                {{ label.title }}
                            </mr-typography>
                        </mr-chips>
                    </div>
                    <CapacityText
                        v-if="tripPackage.showCapacityOnCard"
                        :capacity-text
                        :capacity-status="tripPackage.capacityStatus"
                        class="desktop-only"
                    />
                    <div class="extra-detail mobile-only">
                        <slot name="extra-detail" />
                    </div>
                </div>
            </section>

            <section v-if="!isSelected" class="reserve-info">
                <slot name="highlight" />
                <div class="reserve-options">
                    <TrackingButton
                        v-if="showTrackingButton"
                        :trip-package
                        :show-tooltip="!tripPackage?.reservable && isHovered"
                    />
                    <CapacityText
                        v-if="
                            trip?.reservable &&
                            !hideCapacity &&
                            tripPackage.showCapacityOnCard
                        "
                        :capacity-text
                        :capacity-status="tripPackage.capacityStatus"
                        class="mobile-only"
                    />
                </div>
                <ReservePrice :trip-package :is-selected @reserve="reserve" />
            </section>
        </article>
        <transition name="details">
            <PackageDetails
                v-if="isShowingDetails && !isMobile"
                :trip-package
                :style="{ '--max-height': maxHeight }"
                :hide-invoice
                @reserve="reserve"
            />
        </transition>
    </div>
</template>

<style scoped lang="scss">
.details-leave-active {
    animation: 200ms m-change-animation reverse;
    @include desktop-only {
        animation: 400ms change-animation reverse;
    }
}

.details-enter-active {
    animation: 200ms m-change-animation;
    @include desktop-only {
        animation: 400ms change-animation;
    }
}

@keyframes m-change-animation {
    from {
        transform: translateY(100%);
    }
    to {
        transform: translateY(0%);
    }
}
@keyframes change-animation {
    from {
        max-height: 0;
    }
    to {
        max-height: var(--max-height);
    }
}

.reserve-price {
    align-self: flex-start;
    .price-indicator {
        height: 20px;
    }

    .unit-price {
        height: 24px;
    }
}

.reserve-bell {
    align-self: flex-end;
}

.trip-package-info {
    display: flex;
    flex-direction: column;
    background-color: #ffffff;
    border-radius: 12px;
    overflow: hidden;
    &.shadow {
        @include shadow();
    }
    .card-label {
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: usePalette($p-yellow, 100);
        padding: 2px;
        height: 32px;
        width: 100%;
        border-top-right-radius: 12px;
        border-top-left-radius: 12px;
    }
    .trip-card {
        position: relative;
        display: flex;
        flex-direction: column;
        @include desktop-only {
            flex-direction: row;
            &.single-segment {
                height: 156px;
            }
            &.not-reservable {
                background-color: usePalette($p-cool-gray, $p-200);
                height: 148px;
            }
            &.selected {
                height: 116px;
            }
        }
        @include mobile-only {
            &.not-reservable {
                background-color: usePalette($p-cool-gray, $p-300);
            }
        }
        .trip-info {
            display: flex;
            flex-direction: column;
            padding: 16px;
            gap: 12px;
            height: fit-content;
            overflow-x: hidden;
            @include desktop-only {
                justify-content: center;
                height: 100%;
                padding: 0 12px;
                gap: 0;
                flex: 1 1 auto;
                min-width: 0;
            }
            .segments {
                display: flex;
                align-items: center;
                flex-direction: column;
                gap: 12px;
                .segment {
                    width: 100%;
                }
                @include desktop-only {
                    gap: 0;
                }
            }
            .details {
                display: flex;
                align-items: center;
                justify-content: space-between;
                width: 100%;
                @include desktop-only {
                    gap: 16px;
                    padding: 0 8px;
                }
                .labels {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    overflow-x: hidden;
                    flex: 1 1 auto;
                    min-width: 0; /* important for overflow handling */
                    .label {
                        display: flex;
                        align-items: center;
                        gap: 4px;
                        max-width: 170px;
                        :deep(.font-configs) {
                            overflow: hidden;
                            text-overflow: ellipsis !important;
                        }
                        @include desktop-only {
                            max-width: 250px;
                        }
                    }
                }

                .extra-detail {
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    flex: 0 0 auto;
                    width: fit-content; /* or any fixed size */
                }
                .details-toggle {
                    display: flex;
                    align-items: center;
                    width: 95px;
                    height: 40px;
                    .dropdown-icon {
                        transition: transform 300ms linear;
                    }
                    &.active {
                        .dropdown-icon {
                            transform: rotate(180deg);
                        }
                    }
                }
            }
        }

        .reserve-info {
            display: flex;
            align-items: center;
            padding: 4px 12px;
            height: 60px;
            border-top: 1px solid usePalette($p-cool-gray, $p-200);
            justify-content: space-between;
            @include desktop-only {
                padding: 12px;
                height: auto;
                border-right: 1px solid usePalette($p-cool-gray, $p-200);
                width: 230px;
                flex-shrink: 0;
                flex-direction: column;
                justify-content: space-between;
                align-items: flex-end;
            }

            .reserve-options {
                display: flex;
                align-items: center;
            }
        }
    }
}
</style>
